<template>
	<section v-if="!isServerError" data-testid="trainer-list-component">
		<div class="container">
			<div class="row">
				<div class="col-xs-12 p-t-3">
					<h1 class="h2">{{ heading }}</h1>
				</div>
			</div>
			<div class="row row-xs-bottom">
				<div class="col-xs-12 col-md-8">
					<p>{{ subHeading }}</p>
				</div>
				<div class="col-xs-12 col-md-4 text-md-right">
					<p class="m-b-sm"><b>Location:</b> {{ locationName }}</p>
					<p>
						<span v-if="unauthenticated"><a :href="loginUrl">Log in</a> to view sessions</span>
						<span v-else-if="availableSessions">
							<button class="btn btn-link btn-link-simple" @click="showSessionsAvailableDetails()" data-testid="availableSessionsBtn">View Available Sessions</button>
						</span>
						<span v-else>No Sessions Available</span>
					</p>
				</div>
			</div>
		</div>

		<SearchFilters
			v-if="facetData"
			filterPage="trainerList"
			id="filtersSub"
			ref="filtersSub"
			:filterTypes="filterTypes"
			:facets="facetData"
			:queryFilters="queryFilters"
			prefix="ltf-trainer-list-filter-"
			:savedSearchFilters="[]"
			@changed="handleFiltersChanged"
			itemCssClass="col-xs-12 col-md-6 col-xl-3"
		/>

		<Chiclets
			:selected="searchChiclets"
			:allowFavorites="false"
			@removed="handleRemovedChiclet"
			@clearAllChiclets="handleClearAllChiclets"
		/>

		<div class="container m-t-2">
			<transition-group name="fade-items" tag="div" class="row" @before-leave="beforeLeave">
				<div class="m-b-4 col-xs-12 col-sm-6 col-sm-4 col-md-3 col-xl-3" v-for="instructor in trainers" :key="instructor.profilePath">

					<div class="embed-responsive bio-photo-border" style="padding-bottom: 120%;">
						<img class="embed-responsive-item" :src="instructor.imageUrl" alt="" style="bottom: auto; max-width: 170%; width: 170%; height: auto; top: -2%; left: -35%; right: -35%;"/>
					</div>

					<h3 class="card-title m-t-1">{{ instructor.name }}</h3>
					<a class="btn btn-link btn-link-simple m-b-2" :href="instructor.bioUrl">About Me</a>
					<a class="btn btn-primary btn-sm btn-block" :href="instructor.productUrl">View Services</a>
				</div>
			</transition-group>
		</div>

		<Modal ref="sessionsAvailableDetailsModal" ariaLabelled="sessionsAvailableDetailsModalTitle">
			<template #default>
				<h2 class="h3" data-testid="sessionsAvailableModalTitle">Session Credits Available</h2>
				<table class="table-simple">
					<caption class="sr-only">Session Credits Available</caption>
					<thead>
						<tr>
							<th scope="col">Service</th>
							<th scope="col">Sessions</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="(sessionType, index) in availableSessions" :key="index">
							<td>{{ sessionType.name }}</td>
							<td>{{ sessionType.count }}</td>
						</tr>
					</tbody>
				</table>
				<p class="small">*Products on account with no registration will not be represented in list above.</p>
			</template>
			<template #footer>
				<button type="button" class="btn btn-link" data-dismiss="modal">Cancel</button>
				<button type="button" class="btn btn-primary" @click="scheduleService">Schedule Service</button>
			</template>
		</Modal>

		<!-- Loading Spinner -->
		<div role="alert" aria-live="assertive">
			<transition name="fade" appear>
				<div class="spinner-overlay" data-testid="spinner" v-show="isWaiting">
					<p class="sr-only">Loading…</p>
					<LoadingSpinner />
				</div>
			</transition>
		</div>
	</section>
</template>

<script>
import { defineComponent } from 'vue';
import _ from 'lodash';
import { baseComponentMixin } from '@/mixins/baseComponent.mixin';
import LoadingSpinner from '@/components/schreg-common/LoadingSpinner.vue';
import Modal from '@/components/schreg-common/Modal.vue';
import SearchFilters from '@/components/schreg-common/SearchFilters.vue';
import Chiclets from '@/components/schreg-common/Chiclets.vue';
import { UxSchedulesApi } from '@/apis/uxSchedules.api';
import { StateService } from '@/services/state.service';
import { FilteringService, FilteringServicePages } from '@/services/filtering.service';
import { FilterTypes } from '@/services/filtering.service.config';
import { FrameworkService } from '@/services/framework.service';

export default defineComponent({
	name: 'TrainerListComponent',
	config: {
		key: 'schreg.trainerList',
		devPath: 'trainer-list',
		exported: true
	},
	components: {
		LoadingSpinner,
		Modal,
		SearchFilters,
		Chiclets
	},
	mixins: [
		baseComponentMixin
	],
	data() {
		return {
			heading: '',
			subHeading: '',
			locationName: '',
			availableSessions: null,
			facetData: null,
			trainers: null,
			filterTypes: [],
			searchChiclets: [],
			searchFilters: null,
			queryFilters: [],
			loginUrl: null
		};
	},
	computed: {
		unauthenticated() {
			return !this.userAuthenticated;
		}
	},
	mounted() {
		this.getFrameworkData(this.completeMounting, ['ClubStore', 'ProfileStore', 'UrlStore']);
		// Override overflow: hidden on member site to prevent multiselects getting cut off at footer
		if (document.getElementsByClassName('main-mylt').length > 0) {
			document.getElementsByClassName('main-mylt')[0].style.overflow = 'visible';
		}
	},
	methods: {
		completeMounting() {
			this.loginUrl = `${FrameworkService.globalUrl('loginResource')}`;
			this.locationName = StateService.getLocationName();
			this.heading = StateService.authoredSettings.header || this.locationName + ' Trainers';
			this.subHeading = StateService.authoredSettings.subheader || 'Find your match. Partner with a Personal Trainer whose personality type, specialties and schedule are compatible with your training goals and preferences.';

			this.filterTypes = [
				FilterTypes.serviceMulti,
				FilterTypes.trainerProfilePath
			];

			this.queryFilters = FilteringService.getSelectedFiltersFromQuery(this.filterTypes);
			this.searchFilters = FilteringService.getInitialFilters(FilteringServicePages.trainerList, this.queryFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;

			this.getData().then(response => {
				if (!response) return;

				// Default to first Service
				if (this.searchFilters.service === null) {
					const serviceFilter = response.data.filters.find(f => f.filter === 'service');
					if (serviceFilter && serviceFilter.values && serviceFilter.values.length) {
						this.searchFilters.service = serviceFilter.values[0].value; // Note: This is the first in the data, not the sorted filter
						this.$refs.filtersSub.selectFilterValue(FilterTypes.service, this.searchFilters.service, false); // This will NOT trigger a new API request with the selected service (per Troy, if no services are passed, the API selects the first service the member has sessions for and returns results for that)

						// Display the number of sessions available for this service
						const selectedService = this.availableSessions.find(availableSession => availableSession.value === this.searchFilters.service);
						this.selectedServiceSessions = selectedService ? selectedService.count : null;
					}
				}
			});
		},
		getData() {
			return UxSchedulesApi
				.getTrainerList(this.searchFilters)
				.then(response => {
					if (!response.ok) {
						return this.mountServerError(true, response.data.error);
					}

					this.trainers = response.data.trainers;
					this.facetData = response.data.filters;
					this.availableSessions = _.sortBy(response.data.availableSessions, 'name');

					this.isWaiting = false;
					return Promise.resolve(response);
				});
		},
		showSessionsAvailableDetails() {
			this.$refs.sessionsAvailableDetailsModal.show();
		},
		scheduleService() {
			window.location.href = FrameworkService.accountUrl('ptBooking');
		},
		handleFiltersChanged(updatedFilters) {
			this.isWaiting = true;
			this.searchFilters = FilteringService.getUpdatedFilters(FilteringServicePages.booking, updatedFilters, this.filterTypes);
			this.searchChiclets = this.searchFilters.chiclets;
			this.completeChange();
		},
		completeChange() {
			this.getData().then(() => {

			});
		},
		handleRemovedChiclet(chiclet) {
			this.$refs.filtersSub.deselectFilterValue(chiclet.type, chiclet.value);
		},
		handleClearAllChiclets() {
			for (const filterType of this.filterTypes) {
				StateService.removeQueryStringKey(filterType);
			}
			window.location.reload();
		},
		beforeLeave(el) {
			const { marginLeft, marginTop, width, height } = window.getComputedStyle(el);
			el.style.left = `${el.offsetLeft - parseFloat(marginLeft, 10)}px`;
			el.style.top = `${el.offsetTop - parseFloat(marginTop, 10)}px`;
			el.style.width = width;
			el.style.height = height;
		}
	}
});
</script>

<style lang="scss" scoped>
@import "@/common/styles/loading.scss";
@import "@/common/styles/panels.scss";

.no-results {
	color: $gray-5;
	box-shadow: 0 0 0 1px $gray-11;
	text-align: center;
}
</style>
